import React, { useState, useEffect, useCallback, useRef } from 'react'
import Swal from 'sweetalert2'
import { POST } from '../../../services/api'
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from 'lucide-react'
import './SaleDatesLoteModal.css'

interface ProductWithSaleDates {
  id: number;
  code: string;
  name: string;
  categoryName: string;
  saleDateStart: string | null;
  saleDateEnd: string | null;
  selected?: boolean;
}

interface SaleDatesLoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  pluginspaceId: number;
}

// Funções de conversão - mesmo padrão do create.tsx e update.tsx
const convertMaskedToISO = (maskedDate: string): string | null => {
  if (!maskedDate || maskedDate.trim() === '') return null
  
  const cleaned = maskedDate.trim()
  if (cleaned.length < 16) return null
  
  try {
    const parts = cleaned.match(/(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})/)
    
    if (!parts || parts.length < 6) return null
    
    const day = parts[1]
    const month = parts[2]
    const year = parts[3]
    const hour = parts[4]
    const minute = parts[5]
    
    const dayNum = parseInt(day)
    const monthNum = parseInt(month)
    const yearNum = parseInt(year)
    const hourNum = parseInt(hour)
    const minuteNum = parseInt(minute)
    
    if (dayNum < 1 || dayNum > 31 || monthNum < 1 || monthNum > 12 || 
        hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
      return null
    }
    
    // Cria data SEM forçar UTC - usa o timezone local do navegador
    const date = new Date(yearNum, monthNum - 1, dayNum, hourNum, minuteNum, 0)
    
    if (isNaN(date.getTime())) return null
    
    return date.toISOString()
  } catch (error) {
    console.error('Erro ao converter data:', error)
    return null
  }
}

const convertISOToMasked = (isoDate: string): string => {
  if (!isoDate) return ''
  try {
    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''
    
    // Usa o timezone local do navegador para exibir
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hour = String(date.getHours()).padStart(2, '0')
    const minute = String(date.getMinutes()).padStart(2, '0')
    
    return `${day}/${month}/${year} ${hour}:${minute}`
  } catch (error) {
    console.error('Erro ao converter data para máscara:', error)
    return ''
  }
}

// Função para aplicar máscara DD/MM/YYYY HH:MM enquanto digita
const applyDateTimeMask = (value: string): string => {
  // Remove caracteres que não são dígitos
  let cleaned = value.replace(/\D/g, '')
  
  // Limita a 12 dígitos (DDMMYYYYHHMM)
  cleaned = cleaned.substring(0, 12)
  
  // Aplica a máscara
  let masked = ''
  
  if (cleaned.length >= 1) masked = cleaned.substring(0, 2)
  if (cleaned.length >= 3) masked += '/' + cleaned.substring(2, 4)
  if (cleaned.length >= 5) masked += '/' + cleaned.substring(4, 8)
  if (cleaned.length >= 9) masked += ' ' + cleaned.substring(8, 10)
  if (cleaned.length >= 11) masked += ':' + cleaned.substring(10, 12)
  
  return masked
}

export const SaleDatesLoteModal: React.FC<SaleDatesLoteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  pluginspaceId,
}) => {
  // Estados de filtro
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // Estados de dados
  const [products, setProducts] = useState<ProductWithSaleDates[]>([]);
  const [loading, setLoading] = useState(false);
  const [allSelected, setAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(1)
  const [itemCount, setItemCount] = useState(0)
  const [hasPreviousPage, setHasPreviousPage] = useState(false)
  const [hasNextPage, setHasNextPage] = useState(false)

  // Estados de edição
  const [editMode, setEditMode] = useState<'individual' | 'batch'>('batch');
  const [batchDateStart, setBatchDateStart] = useState('')
  const [batchDateEnd, setBatchDateEnd] = useState('')
  const [showInstructions, setShowInstructions] = useState(false)

  // Cache de edições para persistir ao paginar
  const [editedDatesCache, setEditedDatesCache] = useState<{
    [productId: number]: { saleDateStart?: string; saleDateEnd?: string }
  }>({})

  // Cache de seleção de produtos para persistir ao paginar
  const [selectedProductsCache, setSelectedProductsCache] = useState<Set<number>>(new Set())

  // Refs para debounce
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const editedDatesCacheRef = useRef(editedDatesCache)
  const selectedProductsCacheRef = useRef(selectedProductsCache)

  // Atualizar ref sempre que o cache muda
  useEffect(() => {
    editedDatesCacheRef.current = editedDatesCache
    selectedProductsCacheRef.current = selectedProductsCache
  }, [editedDatesCache, selectedProductsCache])









  // Buscar produtos com filtros (com debounce)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchProducts = useCallback(async (page: number = 1) => {
    if (!pluginspaceId) {
      console.warn('pluginspaceId não definido:', pluginspaceId)
      return
    }

    setLoading(true)
    
    console.log('🔍 Iniciando busca com filtros:', {
      pluginspaceId,
      code: filterCode || undefined,
      name: filterName || undefined,
      categoryName: filterCategory || undefined,
    })

    try {
      const response = await POST(`/product/filter-sale-dates?page=${page}&take=${50}`, {
        pluginspaceId,
        code: filterCode || undefined,
        name: filterName || undefined,
        categoryName: filterCategory || undefined,
      })

      console.log('✅ Resposta da API:', response)
      console.log('📊 Estrutura da resposta - response.data:', response?.data)
      console.log('📊 Estrutura - response.data?.data:', response?.data?.data)

      // A resposta pode vir diretamente em response.data ou em response.data.data
      const productsList = response?.data?.data || response?.data || []
      const metaData = response?.data?.meta || response?.meta || {}
      
      if (Array.isArray(productsList) && productsList.length > 0) {
        const productsData = productsList.map(
          (p: any) => {
            // Verifica se há edição em cache para este produto
            const cachedDates = editedDatesCacheRef.current[p.id]
            // Verifica se o produto foi selecionado antes de paginar
            const isSelected = selectedProductsCacheRef.current.has(p.id)
            
            const mappedProduct = {
              id: p.id,
              code: p.code,
              name: p.name,
              categoryName: p.classification?.name || '-',
              // Usa cache se existir, senão usa dados da API
              saleDateStart: cachedDates?.saleDateStart || (p.saleDateStart ? convertISOToMasked(p.saleDateStart) : ''),
              saleDateEnd: cachedDates?.saleDateEnd || (p.saleDateEnd ? convertISOToMasked(p.saleDateEnd) : ''),
              selected: isSelected,
            }
            
            if (cachedDates) {
              console.log(`🔄 Restaurando do cache - Produto ${p.id}:`,{ cached: cachedDates, mapped: mappedProduct })
            }
            
            if (isSelected) {
              console.log(`☑️ Restaurando seleção - Produto ${p.id}`)
            }
            
            return mappedProduct
          },
        )
        console.log('📦 Produtos carregados:', productsData)
        console.log('📦 Cache atual:', editedDatesCacheRef.current)
        setProducts(productsData)
      } else {
        console.warn('⚠️ Resposta não contém dados:', { response, productsList })
        setProducts([])
      } 

      const nextPage = Number(metaData.page ?? page)
      const nextPageCount = Number(metaData.pageCount ?? 1)
      const nextItemCount = Number(metaData.itemCount ?? 0)

      setCurrentPage(Number.isNaN(nextPage) ? page : nextPage)
      setPageCount(Number.isNaN(nextPageCount) ? 1 : nextPageCount)
      setItemCount(Number.isNaN(nextItemCount) ? 0 : nextItemCount)
      setHasPreviousPage(Boolean(metaData.hasPreviousPage))
      setHasNextPage(Boolean(metaData.hasNextPage))
    } catch (error: any) {
      console.error('❌ Erro ao buscar produtos:', error)
      Swal.fire('Erro', 'Falha ao buscar produtos', 'error')
      console.error('Erro ao buscar produtos:', error)
    } finally {
      setLoading(false)
    }
  }, [pluginspaceId, filterCode, filterName, filterCategory])



  // Disparar busca quando clicar no botão Pesquisar
  const handleSearch = () => {
    // Validar se Nome tem 3+ caracteres quando preenchido
    if (filterName.trim().length > 0 && filterName.trim().length < 3) {
      Swal.fire('Aviso', 'Nome deve ter no mínimo 3 caracteres', 'warning')
      return
    }

    // Validar se Categoria tem 3+ caracteres quando preenchido
    if (filterCategory.trim().length > 0 && filterCategory.trim().length < 3) {
      Swal.fire('Aviso', 'Categoria deve ter no mínimo 3 caracteres', 'warning')
      return
    }

    searchProducts(1);
  }

  // Ao abrir o modal, fazer busca inicial
  useEffect(() => {
    if (isOpen) {
      searchProducts();
    }
  }, [isOpen, searchProducts]);

  // Cleanup debounce
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Toggle seleção individual
  const toggleProductSelection = (index: number) => {
    const productId = products[index].id
    const newProducts = [...products];
    const newSelection = !newProducts[index].selected
    newProducts[index].selected = newSelection;
    setProducts(newProducts);

    // Atualizar cache de seleção
    const newCache = new Set(selectedProductsCacheRef.current)
    if (newSelection) {
      newCache.add(productId)
    } else {
      newCache.delete(productId)
    }
    setSelectedProductsCache(newCache)

    // Atualizar "Selecionar Tudo"
    const allSelectedCheck = newProducts.every((p) => p.selected);
    setAllSelected(allSelectedCheck);
  };

  // Selecionar/Desselecionar tudo
  const toggleSelectAll = () => {
    const newProducts = products.map((p) => ({
      ...p,
      selected: !allSelected,
    }));
    setProducts(newProducts);

    // Atualizar cache de seleção para todos os produtos da página
    const newCache = new Set(selectedProductsCacheRef.current)
    products.forEach(p => {
      if (!allSelected) {
        newCache.add(p.id) // Selecionar todos
      } else {
        newCache.delete(p.id) // Desselecionar todos
      }
    })
    setSelectedProductsCache(newCache)

    setAllSelected(!allSelected);
  };

  // Editar data individual - salva no cache E na tabela
  const updateProductDate = (
    index: number,
    field: 'saleDateStart' | 'saleDateEnd',
    value: string,
  ) => {
    const productId = products[index].id
    
    // Salva no cache para persistir ao paginar
    setEditedDatesCache(prev => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        [field]: value
      }
    }))
    
    console.log('💾 Salvando no cache - Produto:', productId, 'Campo:', field, 'Valor:', value)
    
    // Atualiza a tabela visual
    const newProducts = [...products];
    newProducts[index][field] = value;
    setProducts(newProducts);
  };

  // Atualizar datas
  const hasDataToSave = () => {
    // Em modo batch: precisa ter produtos selecionados
    if (editMode === 'batch') {
      return products.filter((p) => p.selected).length > 0 && (batchDateStart || batchDateEnd)
    }
    // Em modo individual: precisa ter produtos selecionados OU dados em cache
    return products.filter((p) => p.selected).length > 0 || Object.keys(editedDatesCacheRef.current).length > 0
  }

  const updateDates = async () => {
    // Usar cache de seleção para pegar TODOS os produtos selecionados de TODAS as páginas
    const allSelectedIds = Array.from(selectedProductsCacheRef.current)
    
    if (allSelectedIds.length === 0) {
      const message = editMode === 'batch' 
        ? 'Selecione pelo menos um produto'
        : 'Selecione produtos ou edite as datas e tente novamente'
      Swal.fire('Aviso', message, 'warning')
      return
    }

    console.log('📦 Produtos selecionados em TODAS as páginas:', allSelectedIds)

    // Validação de datas
    if (editMode === 'batch') {
      if (batchDateStart && batchDateEnd) {
        const start = new Date(batchDateStart)
        const end = new Date(batchDateEnd)
        if (start > end) {
          Swal.fire(
            'Erro',
            'Data de início não pode ser maior que data de término',
            'error',
          )
          return
        }
      }
    }

    setLoading(true)
    try {
      // Converter datas para ISO usando a mesma função que create.tsx e update.tsx
      const startISO = editMode === 'batch' ? convertMaskedToISO(batchDateStart) : undefined
      const endISO = editMode === 'batch' ? convertMaskedToISO(batchDateEnd) : undefined

      console.log('📤 Enviando atualização em modo', editMode);
      console.log('  Produtos selecionados:', allSelectedIds);
      console.log('  batchDateStart (máscara):', batchDateStart);
      console.log('  batchDateEnd (máscara):', batchDateEnd);

      // Se modo individual, enviar cada produto separadamente
      if (editMode === 'individual') {
        for (const productId of allSelectedIds) {
          // Usa valores do cache
          const cachedDates = editedDatesCacheRef.current[productId]
          const startDate = cachedDates?.saleDateStart || ''
          const endDate = cachedDates?.saleDateEnd || ''
          
          const startISO = convertMaskedToISO(startDate)
          const endISO = convertMaskedToISO(endDate)
          
          console.log(`📤 Atualizando produto ${productId}:`, {
            saleDateStart: startISO,
            saleDateEnd: endISO,
          });
          
          await POST('/product/update-sale-dates-batch', {
            pluginspaceId,
            productIds: [productId],
            saleDateStart: startISO,
            saleDateEnd: endISO,
          })
        }
      } else {
        // Modo batch: enviar todos de uma vez
        const updatePayload = {
          pluginspaceId,
          productIds: allSelectedIds,
          saleDateStart: startISO || undefined,
          saleDateEnd: endISO || undefined,
        }

        console.log('📋 updatePayload final:', updatePayload);
        
        await POST('/product/update-sale-dates-batch', updatePayload)
      }

      Swal.fire(
        'Sucesso',
        `${allSelectedIds.length} produto(s) atualizado(s)`,
        'success',
      )

      onSuccess()
      resetModal()
      onClose()
    } catch (error: any) {
      console.error('❌ Erro ao atualizar datas:', error);
      Swal.fire(
        'Erro',
        error.response?.data?.message || 'Falha ao atualizar datas',
        'error',
      )
      console.error('Erro completo:', error)
    } finally {
      setLoading(false)
    }
  }

  // Resetar modal
  const resetModal = () => {
    setFilterCode('')
    setFilterName('')
    setFilterCategory('')
    setProducts([])
    setAllSelected(false)
    setBatchDateStart('')
    setBatchDateEnd('')
    setEditMode('batch')
    setCurrentPage(1)
    setPageCount(1)
    setItemCount(0)
    setHasPreviousPage(false)
    setHasNextPage(false)
    setEditedDatesCache({}) // Limpa cache de edições
    setSelectedProductsCache(new Set()) // Limpa cache de seleção
  };

  const goToPage = (page: number) => {
    if (page < 1 || page > pageCount || loading) return
    searchProducts(page)
  }

  if (!isOpen) return null;

  return (
    <div className="sale-dates-modal-overlay" onClick={onClose}>
      <div
        className="sale-dates-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sale-dates-modal-header">
          <h2>Editar Datas de Venda em Lote</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Seção de Filtros */}
        <div className="sale-dates-filter-section">
          <h3>Filtros</h3>
          <div className="filter-inputs">
            <input
              type="text"
              placeholder="Código"
              value={filterCode}
              onChange={(e) => setFilterCode(e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Nome"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="filter-input"
            />
            <input
              type="text"
              placeholder="Categoria"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="filter-input"
            />

            <button
              onClick={handleSearch}
              disabled={loading}
              className="search-button"
            >
              {loading ? 'Buscando...' : 'Pesquisar'}
            </button>
          </div>
        </div>

        {/* Tabela de Produtos */}
        <div className="sale-dates-table-section">
          <div className="table-header">
            <label className="select-all-label">
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
              />
              Selecionar Tudo
            </label>
            <span className="product-count">
              {products.length > 0
                ? `${products.filter((p) => p.selected).length} de ${products.length} selecionado(s)`
                : 'Nenhum produto encontrado'}
            </span>
          </div>

          <div className="sale-dates-table-wrapper">
            <table className="sale-dates-table">
              <thead>
                <tr>
                  <th>Selecionar</th>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Categoria</th>
                  <th>Data Início</th>
                  <th>Data Término</th>
                </tr>
              </thead>
              <tbody>
                {products.length > 0 ? (
                  products.map((product, index) => (
                    <tr key={product.id} className={product.selected ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={product.selected || false}
                          onChange={() => toggleProductSelection(index)}
                        />
                      </td>
                      <td>{product.code}</td>
                      <td>{product.name}</td>
                      <td>{product.categoryName || '-'}</td>
                      <td>
                        <input
                          type="text"
                          placeholder="DD/MM/YYYY HH:MM"
                          value={product.saleDateStart || ''}
                          onChange={(e) =>
                            updateProductDate(
                              index,
                              'saleDateStart',
                              applyDateTimeMask(e.target.value),
                            )
                          }
                          maxLength={16}
                          disabled={editMode === 'batch' || !product.selected}
                          className="date-input"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          placeholder="DD/MM/YYYY HH:MM"
                          value={product.saleDateEnd || ''}
                          onChange={(e) =>
                            updateProductDate(
                              index,
                              'saleDateEnd',
                              applyDateTimeMask(e.target.value),
                            )
                          }
                          maxLength={16}
                          disabled={editMode === 'batch' || !product.selected}
                          className="date-input"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="empty-message">
                      Nenhum produto encontrado. Use os filtros para buscar.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="sale-dates-pagination">
            <button
              className="pagination-button"
              onClick={() => goToPage(1)}
              disabled={!hasPreviousPage || loading}
              title="Primeira página"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              className="pagination-button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={!hasPreviousPage || loading}
              title="Página anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="pagination-info">
              Página {currentPage} de {pageCount} • {itemCount} itens
            </span>
            <button
              className="pagination-button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={!hasNextPage || loading}
              title="Próxima página"
            >
              <ChevronRight size={18} />
            </button>
            <button
              className="pagination-button"
              onClick={() => goToPage(pageCount)}
              disabled={!hasNextPage || loading}
              title="Última página"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>

        {/* Accordion de Instruções */}
        <div className="instructions-accordion" style={{ marginBottom: '20px', border: '1px solid #ddd', borderRadius: '4px' }}>
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            style={{
              width: '100%',
              padding: '12px 15px',
              backgroundColor: '#f5f5f5',
              border: 'none',
              borderBottom: showInstructions ? '1px solid #ddd' : 'none',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            <span>ℹ️ Como usar...</span>
            <span style={{ transform: showInstructions ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}>▼</span>
          </button>
          
          {showInstructions && (
            <div style={{ padding: '15px', backgroundColor: '#fafafa', fontSize: '13px', lineHeight: '1.6' }}>
              <h4 style={{ marginTop: '0', marginBottom: '10px' }}>📖 Guia Rápido de Uso</h4>
              
              <div style={{ marginBottom: '15px' }}>
                <strong>🔍 Filtros:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li><strong>Código:</strong> Digite o código do produto (aceita valores parciais)</li>
                  <li><strong>Nome:</strong> Digite o nome do produto (mínimo 3 caracteres)</li>
                  <li><strong>Categoria:</strong> Selecione uma categoria da lista</li>
                  <li>Clique em <strong>"Pesquisar"</strong> para aplicar os filtros</li>
                </ul>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <strong>✏️ Modos de Edição:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>
                    <strong>"Aplicar para Todos os Selecionados":</strong> Selecione múltiplos produtos com checkboxes, 
                    defina uma data de início e fim nos campos acima da tabela, e clique "Atualizar Datas". 
                    <span style={{ color: '#666' }}> A mesma data será aplicada a todos os produtos selecionados.</span>
                  </li>
                  <li style={{ marginTop: '8px' }}>
                    <strong>"Editar Individuais":</strong> Marque cada produto no checkbox, edite as datas individuais na tabela, 
                    e clique "Atualizar Datas". 
                    <span style={{ color: '#666' }}> Cada produto receberá a data que você definiu para ele.</span>
                  </li>
                </ul>
              </div>

              <div style={{ marginBottom: '0' }}>
                <strong>💡 Dicas Importantes:</strong>
                <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                  <li>As datas são <strong>persistidas ao paginar</strong> - não precisa se preocupar em perder dados ao trocar de página</li>
                  <li>Você pode selecionar produtos em <strong>múltiplas páginas</strong> e atualizar todos de uma vez</li>
                  <li>Use a <strong>paginação</strong> na parte inferior da tabela para navegar entre os produtos</li>
                  <li>Formato de data: <strong>DD/MM/YYYY HH:MM</strong> (exemplo: 25/03/2026 10:30)</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Seção de Edição */}
        <div className="sale-dates-edit-section">
          <h3>Modo de Edição</h3>
          <div className="edit-mode-options">
            <label>
              <input
                type="radio"
                value="batch"
                checked={editMode === 'batch'}
                onChange={(e) => setEditMode(e.target.value as 'batch' | 'individual')}
              />
              Aplicar para Todos os Selecionados
            </label>
            <label>
              <input
                type="radio"
                value="individual"
                checked={editMode === 'individual'}
                onChange={(e) => setEditMode(e.target.value as 'batch' | 'individual')}
              />
              Editar Individuais
            </label>
          </div>

          {editMode === 'batch' && (
            <div className="batch-dates-inputs">
              <div className="date-group">
                <label>Data de Início da Venda (DD/MM/YYYY HH:MM):</label>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY HH:MM"
                  value={batchDateStart}
                  onChange={(e) => setBatchDateStart(applyDateTimeMask(e.target.value))}
                  maxLength={16}
                  className="batch-date-input"
                />
              </div>
              <div className="date-group">
                <label>Data de Término da Venda (DD/MM/YYYY HH:MM):</label>
                <input
                  type="text"
                  placeholder="DD/MM/YYYY HH:MM"
                  value={batchDateEnd}
                  onChange={(e) => setBatchDateEnd(applyDateTimeMask(e.target.value))}
                  maxLength={16}
                  className="batch-date-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Botões de Ação */}
        <div className="sale-dates-modal-footer">
          <button onClick={onClose} className="button button-secondary">
            Cancelar
          </button>
          <button
            onClick={updateDates}
            disabled={loading || !hasDataToSave()}
            className="button button-primary"
          >
            {loading ? 'Atualizando...' : 'Atualizar Datas'}
          </button>
        </div>
      </div>
    </div>
  );
};
